import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  getStoredToken,
  setStoredToken,
  registerUnauthorizedHandler,
} from "../../services/api";
import { authService } from "../../services/auth";
import {
  changeLanguage as applyLanguage,
  setSessionAndChangeLanguage,
  clearSessionLanguage,
  getSessionLanguage,
} from "../../i18n";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState("loading"); // 'loading' | 'authenticated' | 'anonymous'
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const clearSession = useCallback(() => {
    setStoredToken(null);
    setUser(null);
    setStatus("anonymous");
    // Session-only language override is tied to the logged-in session; clear
    // it so a subsequent login lands on the user's persisted default.
    clearSessionLanguage();
  }, []);

  // Handle 401 responses from anywhere in the app.
  useEffect(() => {
    registerUnauthorizedHandler(() => {
      clearSession();
      navigate("/login", { replace: true });
    });
  }, [clearSession, navigate]);

  // Session restore on mount. Language priority:
  //   1) sessionStorage override (per-tab, survives refresh in same tab)
  //   2) user.language (persisted default)
  useEffect(() => {
    const token = getStoredToken();
    if (!token) {
      setStatus("anonymous");
      return;
    }
    authService
      .me()
      .then((u) => {
        if (!mounted.current) return;
        setUser(u);
        setStatus("authenticated");
        const sessionLang = getSessionLanguage();
        const target = sessionLang || u.language;
        if (target && target !== i18n.language) {
          applyLanguage(target);
        }
      })
      .catch(() => {
        if (!mounted.current) return;
        clearSession();
      });
  }, []);

  const login = useCallback(
    async (email, password) => {
      const data = await authService.login(email, password);
      setStoredToken(data.token);
      setUser(data.user);
      setStatus("authenticated");
      // Fresh login → discard any leftover session override and honour the
      // user's persisted default language.
      clearSessionLanguage();
      if (data.user.language && data.user.language !== i18n.language) {
        applyLanguage(data.user.language);
      }
      return data.user;
    },
    [i18n.language]
  );

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } catch {
      /* ignore server-side failures — client will still clear its state */
    }
    clearSession();
    navigate("/login", { replace: true });
  }, [clearSession, navigate]);

  /**
   * Session-only language switch (header flag chip). Does NOT touch the
   * backend or the persisted default. Writes to sessionStorage so the choice
   * survives refreshes in the same tab but resets on new tabs / new logins.
   */
  const applySessionLanguage = useCallback((lang) => {
    setSessionAndChangeLanguage(lang);
  }, []);

  /**
   * Persist a new default language (Profile dropdown). Sequence:
   *   1) PATCH /api/auth/me/language
   *   2) applyUser(updatedUser) so the auth context reflects the new default
   *   3) i18n.changeLanguage(newLang) — updates the UI immediately
   *   4) clear sessionStorage — the persisted default is now authoritative
   *   5) return the updated user (caller shows a toast in the NEW language)
   */
  const updateLanguage = useCallback(
    async (lang) => {
      const updated = await authService.updateLanguage(lang);
      setUser(updated);
      applyLanguage(lang);
      clearSessionLanguage();
      return updated;
    },
    []
  );

  const applyUser = useCallback((next) => {
    setUser(next);
  }, []);

  const value = useMemo(
    () => ({
      user,
      status,
      login,
      logout,
      applySessionLanguage,
      updateLanguage,
      applyUser,
      clearSession,
    }),
    [
      user,
      status,
      login,
      logout,
      applySessionLanguage,
      updateLanguage,
      applyUser,
      clearSession,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
